/**
 * Completion is computed from the data entered (not from which screens were visited), so the entity
 * dashboard percentages stay accurate even while required-field enforcement is off for the prototype.
 */
import type {
  BusinessEntity,
  ClientStepId,
  EntitySectionId,
  OnboardingState,
} from '../types/onboarding'
import { miscStateName } from '../data/miscHandledStates'
import { portalDisplayName } from '../data/stateLoginRules'
import {
  credentialRowsForPortal,
  entityPortals,
  isCredentialRowComplete,
} from './credentials'
import { isValidEmailList } from './emailList'
import { SHOW_TAX_PAYMENT_CONTACT } from './prototypeSettings'

export interface Progress {
  /** 0–1 */
  score: number
  /** Human-readable list of what is still missing. */
  missing: string[]
}

const filled = (v: string) => v.trim().length > 0

function fieldsScore(values: boolean[]): number {
  if (values.length === 0) return 1
  return values.filter(Boolean).length / values.length
}

export const ENTITY_SECTIONS: EntitySectionId[] = ['states', 'drlp', 'individuals', 'credentials']

export const ENTITY_SECTION_LABELS: Record<EntitySectionId, string> = {
  states: 'States',
  drlp: 'DRLPs',
  individuals: 'Individual Licensees',
  credentials: 'State Login Credentials',
}

export function entityDetailsMissing(entity: BusinessEntity): string[] {
  const missing: string[] = []
  if (!filled(entity.name)) missing.push('Business entity name')
  if (!filled(entity.residentState)) missing.push('Resident surplus lines license state')
  if (!filled(entity.slLicenseNumber)) missing.push('Surplus lines license number')
  if (!filled(entity.npn)) missing.push('NPN')
  if (!filled(entity.fein)) missing.push('FEIN')
  return missing
}

export function entitySectionProgress(entity: BusinessEntity, section: EntitySectionId): Progress {
  switch (section) {
    case 'states':
      return entity.statesToHandle.length > 0
        ? { score: 1, missing: [] }
        : { score: 0, missing: ['Select at least one state for MISC to handle'] }

    case 'drlp': {
      if (entity.drlps.length === 0) return { score: 0, missing: ['At least one DRLP is required'] }
      const checks: boolean[] = []
      const missing: string[] = []
      entity.drlps.forEach((d, i) => {
        const label = d.fullName.trim() || `DRLP ${i + 1}`
        const row = [
          filled(d.fullName),
          filled(d.jobTitle),
          filled(d.residentState),
          filled(d.slLicenseNumber),
          d.assignedStates.length > 0,
        ]
        checks.push(...row)
        if (row.some((ok) => !ok)) missing.push(`${label}: complete all required fields and assign states`)
      })
      const unassigned = entity.statesToHandle.filter(
        (s) => !entity.drlps.some((d) => d.assignedStates.includes(s)),
      )
      if (unassigned.length > 0) {
        checks.push(false)
        missing.push(`No DRLP assigned for ${unassigned.map(miscStateName).join(', ')}`)
      }
      return { score: fieldsScore(checks), missing }
    }

    case 'individuals': {
      if (entity.individuals.length === 0) {
        return entity.individualsReviewed
          ? { score: 1, missing: [] }
          : // No individuals beyond the DRLPs is a valid answer; continuing marks the section reviewed.
            { score: 0, missing: [] }
      }
      const checks: boolean[] = []
      const missing: string[] = []
      entity.individuals.forEach((p, i) => {
        const row = [filled(p.fullName), filled(p.residentState), filled(p.licenseNumber), filled(p.npn)]
        checks.push(...row)
        if (row.some((ok) => !ok)) missing.push(`${p.fullName.trim() || `Individual ${i + 1}`}: complete all fields`)
      })
      return { score: fieldsScore(checks), missing }
    }

    case 'credentials': {
      if (entity.statesToHandle.length === 0) return { score: 0, missing: ['No states selected yet'] }
      const checks: boolean[] = []
      const missing: string[] = []
      const unanswered = entity.statesToHandle.filter((s) => !entity.writingBusiness[s])
      entity.statesToHandle.forEach((s) => checks.push(Boolean(entity.writingBusiness[s])))
      if (unanswered.length > 0) {
        missing.push(`Answer "currently writing business?" for ${unanswered.map(miscStateName).join(', ')}`)
      }
      for (const { rule, requirement } of entityPortals(entity)) {
        if (requirement !== 'required') continue
        const rows = credentialRowsForPortal(entity, rule.id)
        if (rows.length === 0) {
          checks.push(false)
          missing.push(`${portalDisplayName(rule)}: add a login`)
          continue
        }
        const complete = rows.map(isCredentialRowComplete)
        checks.push(...complete)
        if (complete.some((ok) => !ok)) missing.push(`${portalDisplayName(rule)}: enter every username and password`)
      }
      return { score: fieldsScore(checks), missing }
    }
  }
}

export function entityPercent(entity: BusinessEntity): number {
  const total = ENTITY_SECTIONS.reduce((sum, s) => sum + entitySectionProgress(entity, s).score, 0)
  return Math.round((total / ENTITY_SECTIONS.length) * 100)
}

export type EntityStatus = 'Not started' | 'In progress' | 'Complete'

export function entityStatus(entity: BusinessEntity): EntityStatus {
  const pct = entityPercent(entity)
  if (pct >= 100) return 'Complete'
  if (pct === 0) return 'Not started'
  return 'In progress'
}

export function contactsProgress(state: OnboardingState): Progress {
  const fields: Array<[string, string]> = [
    ['Monthly service invoice email(s)', state.contacts.invoiceEmails],
    ['Client admin email', state.contacts.portalAdminEmails],
  ]
  if (SHOW_TAX_PAYMENT_CONTACT) fields.push(['Tax payment contact email(s)', state.contacts.taxPaymentEmails])
  const missing = fields.filter(([, v]) => !isValidEmailList(v)).map(([label]) => label)
  return { score: fieldsScore(fields.map(([, v]) => isValidEmailList(v))), missing }
}

/** Third-party filer authorizations apply only when some entity has MISC handling that state. */
export function authorizationStatesNeeded(state: OnboardingState): Array<'CA' | 'ID' | 'TX'> {
  const handled = new Set(state.entities.flatMap((e) => e.statesToHandle))
  return (['CA', 'ID', 'TX'] as const).filter((s) => handled.has(s))
}

export const CLIENT_STEP_LABELS: Record<ClientStepId, string> = {
  welcome: 'Welcome',
  checklist: 'Checklist',
  important: 'Important Information',
  entitySetup: 'Business Entities',
  entityDashboard: 'Entity Workflow',
  contacts: 'Contacts',
  attestation: 'Attestation',
  otr: 'OTR Submission',
  review: 'Review & Submit',
  complete: 'Submit',
}

const REVIEW_PREREQUISITES: ClientStepId[] = ['important', 'entitySetup', 'entityDashboard', 'contacts', 'attestation']

export function clientStepProgress(state: OnboardingState, step: ClientStepId): Progress {
  const visited = state.visitedSteps.includes(step)
  switch (step) {
    case 'welcome':
    case 'checklist':
      return { score: visited ? 1 : 0, missing: [] }
    case 'important':
      return state.importantAcknowledged
        ? { score: 1, missing: [] }
        : { score: 0, missing: ['Acknowledge the information above'] }
    case 'entitySetup': {
      if (state.entities.length === 0) return { score: 0, missing: ['Add at least one business entity'] }
      const checks: boolean[] = []
      const missing: string[] = []
      state.entities.forEach((e, i) => {
        const m = entityDetailsMissing(e)
        checks.push(m.length === 0)
        if (m.length > 0) missing.push(`${e.name.trim() || `Entity ${i + 1}`}: ${m.join(', ')}`)
      })
      return { score: fieldsScore(checks), missing }
    }
    case 'entityDashboard': {
      if (state.entities.length === 0) return { score: 0, missing: ['Add at least one business entity'] }
      const incomplete = state.entities.filter((e) => entityPercent(e) < 100)
      const score = state.entities.reduce((sum, e) => sum + entityPercent(e), 0) / (state.entities.length * 100)
      return {
        score,
        missing: incomplete.map((e) => `Finish ${e.name.trim() || 'the unnamed entity'} (${entityPercent(e)}% complete)`),
      }
    }
    case 'contacts':
      return contactsProgress(state)
    case 'attestation':
      return state.attestation.checked || state.attestation.initials.trim().length > 0
        ? { score: 1, missing: [] }
        : { score: 0, missing: ['Check the confirmation box or enter your initials'] }
    // The sample OTR does not block submission: it is repeated under "Next Steps" after Send to MISC.
    // It still counts toward the progress bar.
    case 'otr':
      return { score: state.otrUpload ? 1 : 0, missing: [] }
    case 'review': {
      const missing = REVIEW_PREREQUISITES.filter((s) => clientStepProgress(state, s).missing.length > 0).map(
        (s) => `Complete the ${CLIENT_STEP_LABELS[s]} section`,
      )
      if (!state.certified) missing.push('Certify that the information is accurate and complete')
      return { score: state.certified ? 1 : 0, missing }
    }
    case 'complete':
      return { score: state.submittedAt ? 1 : 0, missing: [] }
  }
}

/** Entity-section progress across all entities (for the top progress bar). */
export function allEntitiesSectionScore(state: OnboardingState, section: EntitySectionId): number {
  if (state.entities.length === 0) return 0
  return (
    state.entities.reduce((sum, e) => sum + entitySectionProgress(e, section).score, 0) / state.entities.length
  )
}
